/**
 * CATÁLOGO DAS HABILIDADES GERADAS — o que o mapa desenha.
 *
 * O template sabe o que ele É (nível, habilidade da matriz, task-model); o catálogo diz
 * como ele se RELACIONA com os outros: de que assuntos trata e o que o aluno precisa
 * dominar antes. Essas relações são julgamento pedagógico, não algo que se derive do
 * código — por isso moram numa tabela à parte, revisável sem mexer em item nenhum.
 *
 * Um teste garante que todo template registrado tem ficha, que todo pré-requisito
 * existe e que as dependências não fecham ciclo.
 */

export type Assunto =
  | 'porcentagem'
  | 'numeros-reais'
  | 'equacoes'
  | 'contagem'
  | 'funcoes'
  | 'sequencias'
  | 'graficos'
  | 'estatistica'
  | 'plano-cartesiano'
  | 'poligonos'
  | 'angulos'
  | 'quadrilateros'
  | 'simetria'
  | 'construcoes'
  | 'grandezas'
  | 'solidos'
  | 'probabilidade';

export type Tema =
  'numeros' | 'algebra' | 'dados' | 'plano' | 'geometria' | 'transformacoes' | 'medidas';

export const ASSUNTO_TXT: Readonly<Record<Assunto, string>> = {
  porcentagem: 'porcentagem',
  'numeros-reais': 'números reais',
  equacoes: 'equações',
  contagem: 'contagem',
  funcoes: 'funções',
  sequencias: 'sequências',
  graficos: 'gráficos',
  estatistica: 'estatística',
  'plano-cartesiano': 'plano cartesiano',
  poligonos: 'polígonos',
  angulos: 'ângulos',
  quadrilateros: 'quadriláteros',
  simetria: 'simetria',
  construcoes: 'construções',
  grandezas: 'grandezas',
  solidos: 'sólidos',
  probabilidade: 'probabilidade',
};

/** Cada assunto pertence a um tema; o tema do item é o do seu assunto principal. */
export const TEMA_DO_ASSUNTO: Readonly<Record<Assunto, Tema>> = {
  porcentagem: 'numeros',
  'numeros-reais': 'numeros',
  equacoes: 'algebra',
  contagem: 'numeros',
  funcoes: 'algebra',
  sequencias: 'algebra',
  graficos: 'dados',
  estatistica: 'dados',
  'plano-cartesiano': 'plano',
  poligonos: 'geometria',
  angulos: 'geometria',
  quadrilateros: 'geometria',
  simetria: 'transformacoes',
  construcoes: 'transformacoes',
  grandezas: 'medidas',
  solidos: 'medidas',
  probabilidade: 'dados',
};

export const TEMAS: readonly { readonly id: Tema; readonly nome: string }[] = [
  { id: 'numeros', nome: 'Números e porcentagem' },
  { id: 'algebra', nome: 'Funções e sequências' },
  { id: 'dados', nome: 'Estatística e probabilidade' },
  { id: 'medidas', nome: 'Grandezas e medidas' },
  { id: 'plano', nome: 'Plano cartesiano' },
  { id: 'geometria', nome: 'Polígonos e ângulos' },
  { id: 'transformacoes', nome: 'Simetria e construções' },
];

export interface FichaHabilidade {
  readonly templateId: string;
  /** Rótulo do nó no mapa: curto o bastante para caber numa linha. */
  readonly nomeCurto: string;
  /** Uma ou duas frases: o que o aluno faz no item. Aparece ao passar o mouse. */
  readonly descricao: string;
  /** O primeiro é o assunto principal — ele decide o tema. */
  readonly assuntos: readonly Assunto[];
  /** Templates que o aluno deveria dominar antes deste. */
  readonly prerequisitos: readonly string[];
}

export const FICHAS: readonly FichaHabilidade[] = [
  {
    templateId: 'h2-04-07-reajustes',
    nomeCurto: 'Reajustes sucessivos',
    descricao:
      'Calcular o efeito de dois aumentos ou descontos percentuais aplicados em sequência — que não se somam.',
    assuntos: ['porcentagem'],
    prerequisitos: [],
  },
  {
    templateId: 'h1-08-01-funcao-grafico',
    nomeCurto: 'Lei e gráfico da afim',
    descricao:
      'Reconhecer, entre cinco gráficos gerados, o que representa a função afim dada pela lei.',
    assuntos: ['funcoes', 'graficos', 'plano-cartesiano'],
    prerequisitos: ['h3-07-K01-malha', 'h1-12-K01-grafico-progressao'],
  },
  {
    templateId: 'h3-30-05-media-agrupada',
    nomeCurto: 'Média no gráfico',
    descricao:
      'Calcular a média de dados num gráfico de colunas, em que cada coluna diz quantas vezes um valor aparece.',
    assuntos: ['estatistica', 'graficos'],
    prerequisitos: ['h3-30-K01-grafico-tendencia'],
  },
  {
    templateId: 'h3-25-07-velocidade-media',
    nomeCurto: 'Velocidade média',
    descricao:
      'Calcular a velocidade média de um percurso em trechos com velocidades diferentes — que não é a média das velocidades.',
    assuntos: ['grandezas'],
    prerequisitos: ['h3-30-05-media-agrupada'],
  },
  {
    templateId: 'h2-20-09-diagonais',
    nomeCurto: 'Diagonais',
    descricao:
      'Contar as diagonais de um polígono como combinações de dois vértices, descontando os lados.',
    assuntos: ['poligonos', 'contagem'],
    prerequisitos: [],
  },
  {
    templateId: 'h2-13-K01-soma-internos',
    nomeCurto: 'Soma dos internos',
    descricao:
      'Calcular a soma dos ângulos internos de um polígono pela divisão em n − 2 triângulos, explorada num seletor de 3 a 8 lados.',
    assuntos: ['angulos', 'poligonos'],
    prerequisitos: ['h2-20-09-diagonais'],
  },
  {
    templateId: 'h2-13-K02-angulo-interno',
    nomeCurto: 'Ângulo interno',
    descricao:
      'Calcular a medida de cada ângulo interno de um polígono regular, a partir da soma dos internos.',
    assuntos: ['angulos', 'poligonos'],
    prerequisitos: ['h2-13-K01-soma-internos'],
  },
  {
    templateId: 'h2-13-K03-soma-externos',
    nomeCurto: 'Soma dos externos',
    descricao:
      'Reconhecer que os ângulos externos de qualquer polígono convexo somam uma volta completa, 360°.',
    assuntos: ['angulos', 'poligonos'],
    prerequisitos: [],
  },
  {
    templateId: 'h2-13-K04-angulo-externo',
    nomeCurto: 'Ângulo externo',
    descricao:
      'Calcular o giro em cada vértice de um polígono regular — o ângulo externo —, repartindo a volta de 360°.',
    assuntos: ['angulos', 'poligonos'],
    prerequisitos: ['h2-13-K03-soma-externos'],
  },
  {
    templateId: 'h1-19-K01-caixa-planificacao',
    nomeCurto: 'Volume pela planificação',
    descricao:
      'Ler as medidas de uma caixa na planificação sobre uma malha em escala e calcular o volume em cm³.',
    assuntos: ['solidos', 'grandezas'],
    prerequisitos: ['h3-07-K01-malha'],
  },
  {
    templateId: 'h3-07-K01-malha',
    nomeCurto: 'Pontos na malha',
    descricao:
      'Marcar os pontos de coordenadas inteiras dentro de uma figura, lendo a escala da malha.',
    assuntos: ['plano-cartesiano'],
    prerequisitos: [],
  },
  {
    templateId: 'h3-11-K02-quadrado-escolha',
    nomeCurto: 'Quadrilátero: escolher',
    descricao:
      'Escolher, entre cinco pares de coordenadas, os vértices que completam um retângulo ou um quadrado inclinado.',
    assuntos: ['plano-cartesiano', 'quadrilateros'],
    prerequisitos: ['h3-07-K01-malha'],
  },
  {
    templateId: 'h3-11-K01-quadrado',
    nomeCurto: 'Quadrilátero: marcar',
    descricao:
      'Marcar na malha os dois vértices que completam um retângulo de lados paralelos aos eixos ou um quadrado inclinado.',
    assuntos: ['plano-cartesiano', 'quadrilateros'],
    prerequisitos: ['h3-11-K02-quadrado-escolha'],
  },
  {
    templateId: 'h3-11-K03-quadrado-coordenadas',
    nomeCurto: 'Quadrilátero: preencher',
    descricao:
      'Completar uma ficha com as coordenadas dos dois vértices que faltam num retângulo ou quadrado, sem alternativas.',
    assuntos: ['plano-cartesiano', 'quadrilateros'],
    prerequisitos: ['h3-11-K02-quadrado-escolha'],
  },
  {
    templateId: 'h3-18-K02-simetria-arrastar',
    nomeCurto: 'Simetria: arrastar',
    descricao:
      'Arrastar os vértices de um triângulo fora do lugar até que ele fique simétrico a outro em relação a uma reta.',
    assuntos: ['simetria'],
    prerequisitos: [],
  },
  {
    templateId: 'h3-18-K01-simetria-malha',
    nomeCurto: 'Simetria na malha',
    descricao:
      'Marcar numa malha vazia os vértices do triângulo simétrico em relação a uma reta, contando quadradinhos até o eixo.',
    assuntos: ['simetria'],
    prerequisitos: ['h3-18-K02-simetria-arrastar'],
  },
  {
    templateId: 'h3-30-K01-grafico-tendencia',
    nomeCurto: 'Tendência do gráfico',
    descricao:
      'Escolher, com um seletor, a altura de uma coluna que mantém o crescimento (ou a queda) de um gráfico.',
    assuntos: ['graficos', 'sequencias'],
    prerequisitos: [],
  },
  {
    templateId: 'h1-12-K01-grafico-progressao',
    nomeCurto: 'Variação constante',
    descricao:
      'Escolher, com um seletor, o valor de uma coluna que mantém constante a variação mensal — uma progressão aritmética.',
    assuntos: ['sequencias', 'graficos'],
    prerequisitos: ['h3-30-K01-grafico-tendencia'],
  },
  {
    templateId: 'k-bissetriz',
    nomeCurto: 'Bissetriz',
    descricao:
      'Construir com régua e compasso a bissetriz de um ângulo; qualquer construção válida é aceita.',
    assuntos: ['construcoes', 'angulos'],
    prerequisitos: [],
  },
  {
    templateId: 'h1-15-K02-trapezio',
    nomeCurto: 'Trapézio retângulo',
    descricao:
      'Construir um trapézio retângulo sobre um segmento dado, com perpendiculares e paralelas.',
    assuntos: ['construcoes', 'quadrilateros'],
    prerequisitos: ['k-bissetriz', 'h3-11-K01-quadrado'],
  },
  {
    templateId: 'h3-20-K01-simetria-construcao',
    nomeCurto: 'Simetria construída',
    descricao:
      'Construir com régua e compasso o triângulo simétrico em relação a uma reta, sem a ferramenta de refletir.',
    assuntos: ['simetria', 'construcoes'],
    prerequisitos: ['h3-18-K01-simetria-malha', 'k-bissetriz'],
  },
  {
    templateId: 'k-erro-equacao',
    nomeCurto: 'Erro: equação',
    descricao:
      'Apontar o primeiro passo errado na resolução de uma equação do 1º grau com parênteses.',
    assuntos: ['equacoes'],
    prerequisitos: [],
  },
  {
    templateId: 'k-erro-reajustes',
    nomeCurto: 'Erro: porcentagens',
    descricao: 'Apontar o primeiro passo errado no cálculo de um aumento seguido de um desconto.',
    assuntos: ['porcentagem'],
    prerequisitos: ['h2-04-07-reajustes'],
  },
  {
    templateId: 'k-erro-velocidade',
    nomeCurto: 'Erro: velocidade média',
    descricao:
      'Apontar o primeiro passo errado no cálculo da velocidade média de um percurso em dois trechos.',
    assuntos: ['grandezas'],
    prerequisitos: ['h3-25-07-velocidade-media'],
  },
  {
    templateId: 'k-erro-angulos',
    nomeCurto: 'Erro: ângulos internos',
    descricao:
      'Apontar o primeiro passo errado no cálculo da soma dos ângulos internos de um polígono.',
    assuntos: ['angulos', 'poligonos'],
    prerequisitos: ['h2-13-K01-soma-internos'],
  },
  {
    templateId: 'h1-01-K01-reta-numerica',
    nomeCurto: 'Reta numérica',
    descricao:
      'Arrastar marcadores com frações, decimais e negativos até os pontos certos de uma reta dividida em partes iguais.',
    assuntos: ['numeros-reais'],
    prerequisitos: [],
  },
  {
    templateId: 'h1-24-K01-escala-mapa',
    nomeCurto: 'Escala do mapa',
    descricao:
      'Medir, com uma régua virtual, a distância entre duas cidades num mapa e convertê-la para quilômetros pela escala gráfica ou numérica.',
    assuntos: ['grandezas'],
    prerequisitos: [],
  },
  {
    templateId: 'h1-36-K01-gangorra',
    nomeCurto: 'Gangorra da média',
    descricao:
      'Mover o apoio de uma régua com caixas até o ponto de equilíbrio — a média dos dados, contando os repetidos.',
    assuntos: ['estatistica'],
    prerequisitos: [],
  },
  {
    templateId: 'h1-22-K01-ampliacao',
    nomeCurto: 'Ampliar na razão k',
    descricao:
      'Arrastar os vértices de uma cópia até que ela seja a figura ampliada ou reduzida na razão pedida.',
    assuntos: ['grandezas', 'poligonos'],
    prerequisitos: ['h3-18-K02-simetria-arrastar'],
  },
  {
    templateId: 'h1-30-K01-dados-evento',
    nomeCurto: 'Eventos com dois dados',
    descricao:
      'Marcar, na tabela dos 36 resultados de dois dados, todos os que formam um evento — sem esquecer que (2, 5) e (5, 2) são resultados diferentes.',
    assuntos: ['probabilidade', 'contagem'],
    prerequisitos: [],
  },
  {
    templateId: 'h3-17-K01-planificacoes-cubo',
    nomeCurto: 'Planificações do cubo',
    descricao:
      'Reconhecer, entre seis figuras de seis quadrados, todas as que, dobradas, fecham um cubo.',
    assuntos: ['solidos'],
    prerequisitos: [],
  },
  {
    templateId: 'h1-08-K02-ajustar-reta',
    nomeCurto: 'Ajustar a reta',
    descricao:
      'Mover os controles de a e de b até o gráfico de y = ax + b passar por dois pontos dados.',
    assuntos: ['funcoes', 'plano-cartesiano'],
    prerequisitos: ['h1-08-01-funcao-grafico'],
  },
  {
    templateId: 'h1-12-K02-expressao',
    nomeCurto: 'Expressão da tabela',
    descricao:
      'Escrever o termo geral de uma progressão aritmética ou a lei de uma função afim a partir de uma tabela; vale qualquer forma equivalente.',
    assuntos: ['sequencias', 'funcoes'],
    prerequisitos: ['h1-12-K01-grafico-progressao'],
  },
  {
    templateId: 'h1-01-K02-ordenar',
    nomeCurto: 'Ordenar reais',
    descricao:
      'Pôr em ordem crescente cartões com decimais, negativos e frações, desviando das armadilhas de comparação.',
    assuntos: ['numeros-reais'],
    prerequisitos: ['h1-01-K01-reta-numerica'],
  },
  {
    templateId: 'h1-37-K01-eixo-truncado',
    nomeCurto: 'Eixo truncado',
    descricao:
      'Julgar uma manchete tirada de um gráfico cujo eixo vertical não começa em zero, movendo o início do eixo até o zero.',
    assuntos: ['graficos', 'estatistica'],
    prerequisitos: ['h3-30-K01-grafico-tendencia'],
  },
  {
    templateId: 'h3-30-K02-montar-colunas',
    nomeCurto: 'Montar colunas',
    descricao:
      'Levantar as colunas de um gráfico até os valores de uma tabela, contando as linhas da grade.',
    assuntos: ['graficos', 'estatistica'],
    prerequisitos: [],
  },
  {
    templateId: 'h1-34-K01-setor-angulo',
    nomeCurto: 'Setor pelo ângulo',
    descricao: 'Descobrir quantas pessoas uma fatia representa a partir do ângulo do setor.',
    assuntos: ['graficos', 'estatistica'],
    prerequisitos: [],
  },
  {
    templateId: 'h3-33-K01-media-alvo',
    nomeCurto: 'Média alvo',
    descricao: 'Mexer numa coluna do gráfico até a média do conjunto ser um valor pedido.',
    assuntos: ['graficos', 'estatistica'],
    prerequisitos: ['h3-30-K02-montar-colunas', 'h3-30-05-media-agrupada'],
  },
  {
    templateId: 'h1-32-K01-urna',
    nomeCurto: 'Urna do sorteio',
    descricao:
      'Acrescentar bolas escuras à urna até a chance de sortear uma escura ser a fração pedida.',
    assuntos: ['probabilidade'],
    prerequisitos: [],
  },
  {
    templateId: 'h2-15-K01-ladrilhar',
    nomeCurto: 'Ladrilhar o plano',
    descricao:
      'Marcar os polígonos regulares cujo ângulo interno cabe um número inteiro de vezes em 360°.',
    assuntos: ['poligonos', 'angulos'],
    prerequisitos: ['h2-13-K02-angulo-interno'],
  },
  {
    templateId: 'h1-10-K01-balanca',
    nomeCurto: 'Balança de dois pratos',
    descricao:
      'Resolver uma equação tirando a mesma coisa dos dois pratos, até sobrar um saco sozinho.',
    assuntos: ['equacoes'],
    prerequisitos: ['k-erro-equacao'],
  },
];

const POR_ID = new Map(FICHAS.map((f) => [f.templateId, f]));

export function fichaDe(templateId: string): FichaHabilidade | undefined {
  return POR_ID.get(templateId);
}

export const temaDe = (f: FichaHabilidade): Tema => TEMA_DO_ASSUNTO[f.assuntos[0] as Assunto];

/** Quem depende diretamente de `templateId`. */
export function dependentes(
  templateId: string,
  fichas: readonly FichaHabilidade[] = FICHAS,
): string[] {
  return fichas.filter((f) => f.prerequisitos.includes(templateId)).map((f) => f.templateId);
}

/**
 * Camada de cada habilidade: o comprimento da MAIOR cadeia de pré-requisitos que leva
 * até ela. Sem pré-requisito, camada 0. Lança se as dependências fecharem um ciclo —
 * um ciclo quer dizer que nenhuma das habilidades envolvidas poderia ser a primeira.
 */
export function camadas(fichas: readonly FichaHabilidade[] = FICHAS): Map<string, number> {
  const porId = new Map(fichas.map((f) => [f.templateId, f]));
  const memo = new Map<string, number>();
  const visitando = new Set<string>();
  const nivel = (id: string): number => {
    const m = memo.get(id);
    if (m !== undefined) return m;
    if (visitando.has(id)) throw new Error(`ciclo de pré-requisitos passando por ${id}`);
    visitando.add(id);
    const f = porId.get(id);
    const v = f && f.prerequisitos.length > 0 ? 1 + Math.max(...f.prerequisitos.map(nivel)) : 0;
    visitando.delete(id);
    memo.set(id, v);
    return v;
  };
  for (const f of fichas) nivel(f.templateId);
  return memo;
}

export function temCiclo(fichas: readonly FichaHabilidade[] = FICHAS): boolean {
  try {
    camadas(fichas);
    return false;
  } catch {
    return true;
  }
}

/** Assuntos em comum entre duas fichas. */
export const assuntosEmComum = (a: FichaHabilidade, b: FichaHabilidade): Assunto[] =>
  a.assuntos.filter((x) => b.assuntos.includes(x));
